<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Modal, Text, Alert } from '@Shelly/shelly-design-system';
	import type { EnergyTariff, EnergyTariffCost } from 'src/types/EnergyTariff';

	export let showModal: boolean = false;
	export let tariff: EnergyTariff;
	export let totalUsage: number = 2500; // From usage.annualTotal in request

	const handleClose = () => {
		showModal = false;
	};

	// Helper to parse amount from cost info
	const parseAmount = (value: string | number | undefined): number => {
		if (typeof value === 'number') return value;
		if (typeof value === 'string') return parseFloat(value) || 0;
		return 0;
	};

	// Helper to format currency
	const formatCurrency = (amount: number): string => {
		return amount.toFixed(2).replace('.', ',');
	};

	// Extract cost data
	let cost = tariff.cost as EnergyTariffCost | undefined;
	let totalCostAmount = cost?.totalCost?.amount
		? parseAmount(cost.totalCost.amount)
		: tariff.totalCosts || 0;
	// Unit price: if from cost.unitPrice, it's already in cents, otherwise use tariff.pricePerKwh (in EUR)
	let unitPriceCentsFromCost = cost?.unitPrice?.amount ? parseAmount(cost.unitPrice.amount) : null;
	let unitPriceAmount =
		unitPriceCentsFromCost !== null ? unitPriceCentsFromCost / 100 : tariff.pricePerKwh || 0;
	let unitPriceCents =
		unitPriceCentsFromCost !== null ? unitPriceCentsFromCost : unitPriceAmount * 100;
	let fixedCostsAmount = cost?.fixedCosts?.amount
		? parseAmount(cost.fixedCosts.amount)
		: tariff.basePrice || 0;
	let fixedCostsMonthlyAmount = cost?.fixedCostsMonthly?.amount
		? parseAmount(cost.fixedCostsMonthly.amount)
		: fixedCostsAmount / 12;
	let usageCostsAmount = cost?.usageCosts?.amount
		? parseAmount(cost.usageCosts.amount)
		: totalUsage * unitPriceAmount;
	let totalWithoutBonus = usageCostsAmount + fixedCostsAmount;
	let averageMonthlyWithoutBonus = totalWithoutBonus / 12;

	// Extract bonuses
	let bonuses = cost?.bonuses || [];
	let sumOfBonuses = cost?.sumOfOneTimeBonuses?.amount
		? parseAmount(cost.sumOfOneTimeBonuses.amount)
		: bonuses.reduce((sum, bonus) => sum + parseAmount(bonus.amount), 0);
	let totalWithBonus = totalWithoutBonus - sumOfBonuses;
	let averageMonthlyWithBonus = cost?.averageMonthlyCostInfoIncludingBonuses?.amount
		? parseAmount(cost.averageMonthlyCostInfoIncludingBonuses.amount)
		: totalWithBonus / 12;

	// Estimated installment
	let estimatedInstallment = cost?.estimatedMonthlyCostInfo?.amount
		? parseAmount(cost.estimatedMonthlyCostInfo.amount)
		: averageMonthlyWithoutBonus;
	let estimatedInstallmentComment = cost?.estimatedMonthlyCostInfo?.comment || '';
	let paymentMonthsPerYear = cost?.estimatedMonthlyCostInfo?.paymentMonthsPerYear || '12';
</script>

<Modal
	{showModal}
	on:close={handleClose}
	backdropClose={true}
	title={$format('energy_price_monthly_average_title', {
		default: 'Durchschnitt pro Monat im 1. Jahr',
		locale: 'de',
	})}
>
	<svelte:fragment slot="content">
		{#if cost}
			<div class="cost-breakdown">
				<!-- Cost Calculation without Bonus -->
				<div class="cost-section">
					<Text styleType="body-small" class="section-label mb-s">
						{$format('energy_price_cost_calculation_without_bonus', {
							default: 'Kostenberechnung ohne Bonus',
							locale: 'de',
						})}
					</Text>
					<div class="cost-table">
						<div class="cost-row">
							<Text styleType="body-small" class="cost-label">
								{$format('energy_price_your_consumption', {
									default: 'Ihre Verbrauchsangabe',
									locale: 'de',
								})}
							</Text>
							<Text styleType="body" class="cost-value">
								{totalUsage.toFixed(1)} kWh / {$format('label_year', {
									default: 'Jahr',
									locale: 'de',
								})}
							</Text>
						</div>
						<div class="cost-row">
							<Text styleType="body-small" class="cost-label">
								× {$format('energy_price_energy_price_per_kwh', {
									default: 'Arbeitspreis pro kWh',
									locale: 'de',
								})}
							</Text>
							<Text styleType="body" class="cost-value">
								{unitPriceCents.toFixed(2)}
								{$format('label_cent_per_kwh', {
									default: 'Cent/kWh',
									locale: 'de',
								})}
							</Text>
						</div>
						<div class="cost-row">
							<Text styleType="body-small" class="cost-label">
								= {$format('energy_price_total_energy_price', {
									default: 'Arbeitspreis gesamt',
									locale: 'de',
								})}
							</Text>
							<Text styleType="body" class="cost-value price-bold">
								{formatCurrency(usageCostsAmount)} € / {$format('label_year', {
									default: 'Jahr',
									locale: 'de',
								})}
							</Text>
						</div>
						<div class="cost-row">
							<Text styleType="body-small" class="cost-label">
								+ {$format('energy_price_basic_price', {
									default: 'Grundpreis',
									locale: 'de',
								})}
							</Text>
							<Text styleType="body" class="cost-value">
								{formatCurrency(fixedCostsAmount)} € / {$format('label_year', {
									default: 'Jahr',
									locale: 'de',
								})}
								({formatCurrency(fixedCostsMonthlyAmount)} € / {$format('option_month', {
									default: 'Monat',
									locale: 'de',
								})})
							</Text>
						</div>
						<div class="cost-row cost-row-total">
							<Text styleType="body-small" class="cost-label">
								= {$format('energy_price_total_price_first_year_without_bonus', {
									default: 'Gesamtpreis im ersten Jahr ohne Bonus',
									locale: 'de',
								})}
							</Text>
							<Text styleType="body" class="cost-value price-bold">
								{formatCurrency(totalWithoutBonus)} € / {$format('label_year', {
									default: 'Jahr',
									locale: 'de',
								})}
							</Text>
						</div>
						<div class="cost-row cost-row-average">
							<Text styleType="body-small" class="cost-label">
								{$format('label_average_per_month', {
									default: 'Durchschnitt pro Monat',
									locale: 'de',
								})}
							</Text>
							<Text styleType="body" class="cost-value price-bold">
								{formatCurrency(averageMonthlyWithoutBonus)} € / {$format(
									'option_month',
									{
										default: 'Monat',
										locale: 'de',
									},
								)}
							</Text>
						</div>
					</div>
				</div>

				<!-- Bonuses Section -->
				{#if bonuses.length > 0}
					<div class="cost-section">
						<Text styleType="body-small" class="section-label mb-s">
							{$format('energy_price_bonuses', { default: 'Boni', locale: 'de' })}
						</Text>
						<div class="cost-table">
							{#each bonuses as bonus}
								<div class="cost-row">
									<Text styleType="body-small" class="cost-label">
										- {bonus.caption?.text || bonus.content?.text || ''}
									</Text>
									<Text styleType="body" class="cost-value">
										{bonus.amount ? formatCurrency(parseAmount(bonus.amount)) : ''} €
									</Text>
								</div>
							{/each}
						</div>
					</div>
				{/if}

				<!-- Cost Calculation with Bonus -->
				{#if sumOfBonuses > 0}
					<div class="cost-section">
						<Text styleType="body-small" class="section-label mb-s">
							{$format('energy_price_cost_calculation_with_bonus', {
								default: 'Kostenberechnung mit Bonus',
								locale: 'de',
							})}
						</Text>
						<div class="cost-table">
							<div class="cost-row cost-row-total">
								<Text styleType="body-small" class="cost-label">
									= {$format('energy_price_total_price_first_year_with_bonus', {
										default: 'Gesamtpreis im ersten Jahr inkl. Bonus',
										locale: 'de',
									})}
								</Text>
								<Text styleType="body" class="cost-value price-bold">
									{formatCurrency(totalWithBonus)} € / {$format('label_year', {
										default: 'Jahr',
										locale: 'de',
									})}
								</Text>
							</div>
							<div class="cost-row cost-row-average">
								<Text styleType="body-small" class="cost-label">
									{$format('label_average_per_month', {
										default: 'Durchschnitt pro Monat',
										locale: 'de',
									})}
								</Text>
								<Text styleType="body" class="cost-value price-bold">
									{formatCurrency(averageMonthlyWithBonus)} € / {$format(
										'option_month',
										{
											default: 'Monat',
											locale: 'de',
										},
									)}
								</Text>
							</div>
						</div>
					</div>
				{/if}

				<!-- Estimated Installment -->
				{#if cost.estimatedMonthlyCostInfo}
					<div class="cost-section">
						<Text styleType="body-small" class="section-label mb-s">
							{$format('energy_price_estimated_installment', {
								default: 'Geschätzter Abschlag',
								locale: 'de',
							})}
						</Text>
						<div class="cost-table">
							<div class="cost-row">
								<Text styleType="body-small" class="cost-label">
									{$format('energy_price_estimated_monthly_debit', {
										default: 'Geschätzter Abschlag',
										locale: 'de',
									})}
									({$format('energy_price_monthly_account_debit', {
										default: 'monatliche Kontobelastung',
										locale: 'de',
									})})
								</Text>
								<Text styleType="body" class="cost-value price-bold">
									ca. {formatCurrency(estimatedInstallment)} €
								</Text>
							</div>
						</div>
						{#if estimatedInstallmentComment}
							<Alert
								isHeadingBold={true}
								class="mb-s"
								type="info"
								message={estimatedInstallmentComment}
							/>
						{/if}
					</div>
				{/if}

				<!-- Footnote -->
				<div class="cost-footnote">
					<Text styleType="body-extra-small" class="footnote-text">
						* {$format('energy_price_average_monthly_footnote', {
							default:
								'In die Durchschnittkosten pro Monat werden alle Boni einbezogen und auf einer Basis von 12 Monaten pro Jahr berechnet. Dieser Wert dient als Vergleichswert. Die tatsächliche Anzahl der Abschläge kann variieren. Der monatlich zu begleichende Abschlag weicht von den hier berechneten Durchschnittskosten pro Monat möglicherweise ab.',
							locale: 'de',
						})}
					</Text>
				</div>
			</div>
		{/if}
	</svelte:fragment>
</Modal>

<style>
	.cost-breakdown {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-l);
	}

	.cost-section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.section-label {
		font-weight: 600;
		color: var(--color-text-primary, #000000);
		margin: 0;
	}

	.cost-table {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.cost-row {
		display: grid;
		grid-template-columns: 1fr auto;
		gap: var(--spacing-m);
		align-items: start;
		padding: 4px 0;
	}

	.cost-row-total {
		border-top: 1px solid var(--color-border, #e5e7eb);
		padding-top: 8px;
		margin-top: 4px;
	}

	.cost-row-average {
		border-top: 1px solid var(--color-border, #e5e7eb);
		padding-top: 8px;
		margin-top: 4px;
	}

	.cost-label {
		color: var(--color-text-secondary, #6b7280);
		margin: 0;
		text-align: left;
	}

	.cost-value {
		color: var(--color-text-primary, #000000);
		margin: 0;
		text-align: right;
		white-space: nowrap;
	}

	.cost-value.price-bold {
		font-weight: 700;
	}

	.cost-footnote {
		padding-top: var(--spacing-m);
		border-top: 1px solid var(--color-border, #e5e7eb);
	}

	.footnote-text {
		color: var(--color-text-secondary, #6b7280);
		margin: 0;
		line-height: 1.5;
		font-size: 10px;
	}
</style>
